/**
 * GenericDialog can be used to open / close dialog.generic-dialog elements on Bilia page.
 *
 * Dialog class can be accessed by importing it to where it needed by
 * import GenericDialog from './modules/generic-dialog'
 *
 * GenericDialog.instances[<elementId>].open();
 * GenericDialog.instances[<elementId>].close();
 *
 * Dialog can be opened with a anchor in URL address. Let's say that you have dialog#test-dialog then you
 * can open it by opening page in https://bilia-admin.stage.crasman.fi/fi/dev/components/#test-dialog. Now
 * dialog should be open after page load. This helps dialog development by not have to open dialog manually on
 * each page load.
 */
export default class GenericDialog {

  /**
   * Instances of dialog class are hold this object.
   * Where key is dialog element id attribute and value is GenericDialog instance.
   *
   * @type {object}
   */
  static instances = {};

  /**
   * @constructor
   * @param {DialogElement} dialogElement context
   */
  constructor( dialogElement ) {
    this.dom = {
      element: dialogElement,
      closeButton: dialogElement.querySelector('button.dialog-close'),
      content: dialogElement.querySelector('.dialog-content'),
    };

    this._attachEventListeners();

    // Open element with a backdrop by using native showModal() function.
    // Using open attribute won't show ::backdrop pseudo element.
    if( /*this.dom.element.getAttribute('show') !== null  ||*/
      window.location.hash.includes(this.dom.element.id) ) {
      this.open();
    }
  }

  /**
   * Show dialog element, internally call dialog element open function.
   */
  open() {
    this.dom.element.showModal();
  }

  /**
   * Close dialog element, internally call dialog element open function.
   */
  close() {
    this.dom.element.close();
    history.replaceState(null, null, ' ');
  }

  /**
   * Attach event listeners
   * @private
   */
  _attachEventListeners() {
    this.dom?.closeButton?.addEventListener('keydown', this._onKeyDown.bind(this) );
    this.dom?.closeButton?.addEventListener('click', this._closeButtonClicked.bind(this) );

    // Close modal on back button and open on forward
    window.addEventListener('hashchange', () => {
      const matchingHash = window.location.hash.substring(1) === this.dom.element.id;
      if (matchingHash && !this.dom.element.open) {
        this.open();
      } else if (!matchingHash && this.dom.element.open) {
        this.close();
      }
    });
  }

  /**
   * Called on key press
   * @param {Event} event key press event
   */
  _onKeyDown(event) {
    if( event.key === 'Escape' ) {
      this.close();
    }
  }

  /**
   * Close button clicked
   * @private
   */
  _closeButtonClicked() {
    this.close();
  }

  /**
   * Initializes dialog elements
   */
  static init() {
    const dialogs = document.querySelectorAll('dialog.generic-dialog');
    dialogs.forEach( dialog => {
      GenericDialog.instances[dialog.id] = new GenericDialog( dialog );
    });

    // Listen clicks element[data-target="dialogid"] to open the dialogs
    document.addEventListener('click', (event) => {
      const target = event.target.dataset.target;
      GenericDialog.instances[target]?.open();
    });
  }
}
